import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import usuarioService from '../../services/UsuarioService';
import PasswordInputWithToggle from './PasswordInputWithToggle';
import './UserAccountMenu.css';

const getDisplayName = (user) => user?.displayName || user?.name || user?.email || 'Usuario';
const getUserEmail = (user) => user?.email || 'usuario@utn.edu.ar';

const UserAccountMenu = ({ extraItems = [] }) => {
  const { user, logout, updateUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);

  const canCloseModal = !passwordLoading;

  const resetPasswordForm = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setModalMessage(null);
  };

  const openPasswordModal = () => {
    setIsOpen(false);
    resetPasswordForm();
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    if (!canCloseModal) return;
    setShowPasswordModal(false);
    resetPasswordForm();
  };

  const handlePasswordInputChange = (field, value) => {
    setPasswordForm((prev) => ({
      ...prev,
      [field]: value
    }));
    setModalMessage(null);
  };

  const handlePasswordSubmit = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!passwordForm.currentPassword.trim()) {
      setModalMessage({ type: 'error', text: 'La contraseña actual no puede estar vacía.' });
      return;
    }

    if (!passwordForm.newPassword.trim()) {
      setModalMessage({ type: 'error', text: 'La nueva contraseña no puede estar vacía.' });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setModalMessage({ type: 'error', text: 'La nueva contraseña debe tener al menos 6 caracteres.' });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setModalMessage({ type: 'error', text: 'Las contraseñas nuevas no coinciden.' });
      return;
    }

    setPasswordLoading(true);
    setModalMessage(null);

    try {
      const response = await usuarioService.updatePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword
      );

      if (response && updateUser) {
        const roleName = response?.nombreRol || response?.NombreRol || user?.roleName || user?.role;
        updateUser({
          token: response?.token || response?.Token || user?.token,
          refreshToken: response?.refreshToken || response?.RefreshToken || user?.refreshToken,
          requirePasswordChange: Boolean(response?.requirePasswordChange ?? response?.RequirePasswordChange ?? false),
          roleId: response?.idRol || response?.IdRol || user?.roleId,
          roleName,
          role: roleName || user?.role
        });
      }

      resetPasswordForm();
      setModalMessage({ type: 'success', text: 'Contraseña actualizada exitosamente.' });
      setShowPasswordModal(false);
    } catch (error) {
      setModalMessage({
        type: 'error',
        text: error.message || 'Error al actualizar la contraseña.'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  const handleExtraItemClick = (item) => {
    setIsOpen(false);
    if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <>
      <div className="user-menu user-account-menu">
        <button
          type="button"
          className="user-menu-trigger"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          title="Menú de usuario"
        >
          <div className="user-avatar">
            {user?.pictureUrl ? (
              <img src={user.pictureUrl} alt="" />
            ) : (
              <i className="fas fa-user"></i>
            )}
          </div>
          <i className="fas fa-chevron-down"></i>
        </button>

        {isOpen && (
          <div className="user-menu-dropdown" role="menu">
            <div className="dropdown-header">
              <span className="user-name">{getDisplayName(user)}</span>
              <span className="user-email">{getUserEmail(user)}</span>
            </div>
            <div className="dropdown-divider"></div>
            <button className="dropdown-item" onClick={openPasswordModal} type="button" role="menuitem">
              <i className="fas fa-key"></i>
              Actualizar Password
            </button>
            {extraItems.map((item) => (
              <button
                className="dropdown-item"
                key={item.key || item.label}
                onClick={() => handleExtraItemClick(item)}
                type="button"
                role="menuitem"
              >
                {item.icon && <i className={item.icon}></i>}
                {item.label}
              </button>
            ))}
            <div className="dropdown-divider"></div>
            <button className="dropdown-item logout-item" onClick={handleLogout} type="button" role="menuitem">
              <i className="fas fa-sign-out-alt"></i>
              Cerrar Sesión
            </button>
          </div>
        )}
      </div>

      {showPasswordModal && (
        <div
          className="modal-overlay user-account-modal-overlay"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            closePasswordModal();
          }}
        >
          <div className="modal-content user-account-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-key"></i>
                Actualizar Contraseña
              </h3>
              <button
                className="modal-close-btn"
                onClick={closePasswordModal}
                disabled={!canCloseModal}
                type="button"
                title="Cerrar"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handlePasswordSubmit}>
              <div className="modal-body">
                {modalMessage && (
                  <div className={`user-account-modal-message ${modalMessage.type}`}>
                    {modalMessage.text}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="account-current-password">Contraseña Actual</label>
                  <PasswordInputWithToggle
                    id="account-current-password"
                    className="form-input"
                    value={passwordForm.currentPassword}
                    onChange={(event) => handlePasswordInputChange('currentPassword', event.target.value)}
                    placeholder="Ingresa tu contraseña actual"
                    disabled={passwordLoading}
                    autoComplete="current-password"
                    label="contraseña actual"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="account-new-password">Nueva Contraseña</label>
                  <PasswordInputWithToggle
                    id="account-new-password"
                    className="form-input"
                    value={passwordForm.newPassword}
                    onChange={(event) => handlePasswordInputChange('newPassword', event.target.value)}
                    placeholder="Ingresa tu nueva contraseña"
                    disabled={passwordLoading}
                    autoComplete="new-password"
                    label="nueva contraseña"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="account-confirm-password">Repetir Nueva Contraseña</label>
                  <PasswordInputWithToggle
                    id="account-confirm-password"
                    className="form-input"
                    value={passwordForm.confirmPassword}
                    onChange={(event) => handlePasswordInputChange('confirmPassword', event.target.value)}
                    placeholder="Repite tu nueva contraseña"
                    disabled={passwordLoading}
                    autoComplete="new-password"
                    label="repetir nueva contraseña"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={closePasswordModal}
                  disabled={!canCloseModal}
                  type="button"
                >
                  Cancelar
                </button>
                <button className="btn btn-primary" disabled={passwordLoading} type="submit">
                  {passwordLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      Actualizando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Actualizar
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default UserAccountMenu;
